if (!customElements.get('password-protected-video-series')) {
    class PasswordProtectedVideoSeries extends HTMLElement {
        constructor() {
            super();
            this.password = window.ppvsPassword;
            this.isUnlocked = false;
        }

        connectedCallback() {
            this.passwordInput = this.querySelector('.ai-video-series-password-input');
            this.passwordButton = this.querySelector('.ai-video-series-password-button');
            this.errorMessage = this.querySelector('.ai-video-series-error');
            this.successMessage = this.querySelector('.ai-video-series-success');
            this.content = this.querySelector('.ai-video-series-content');
            this.passwordForm = this.querySelector('.ai-video-series-password-form');

            this.setupEventListeners();
            this.clearCookieIfRequested();
            this.ensureCookie();

            if (window.Shopify && Shopify.designMode) {
                // In theme editor: bypass password and delays
                this.setCookie({ granted: true, grantedAt: Date.now() - 365*24*60*60*1000, viewed: this.getCookie()?.viewed || {} });
                this.unlockContent(true);
            } else {
                this.checkStoredAccess();
            }
            this.applyEpisodeLocks();
        }

        setupEventListeners() {
            this.passwordButton.addEventListener('click', () => this.checkPassword());

            this.passwordInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    this.checkPassword();
                }
            });

            this.passwordInput.addEventListener('input', () => {
                this.hideMessages();
            });
        }

        checkStoredAccess() {
            const data = this.getCookie();

            if (data && data.granted) {
                this.hideExternalForm();
                this.unlockContent(true);
            }
        }

        // Hide external Shopify app form on initial load (if access already granted)
        hideExternalForm() {
            const externalForm = document.querySelector('.shopify-section .shopify-app-block div[data-forms-id="forms-root-701541"]');
            console.log(externalForm);

            if (externalForm) externalForm.closest(".shopify-app-block").style.display = 'none';
        }

        checkPassword() {
            const enteredPassword = this.passwordInput.value.trim();

            if (!enteredPassword) {
                this.showError('Please enter a password');
                return;
            }

            if (enteredPassword === atob(this.password)) {
                this.setCookie({ granted: true, grantedAt: Date.now(), viewed: this.getCookie()?.viewed || {} });
                this.unlockContent();
            } else {
                this.showError('Incorrect password. Please try again.');
                this.passwordInput.value = '';
            }
        }

        unlockContent(silent) {
            this.isUnlocked = true;
            if (!silent) this.showSuccess('Access granted! Welcome to the series.');

            setTimeout(() => {
                if (this.passwordForm) this.passwordForm.style.display = 'none';
                if (this.content) this.content.classList.add('unlocked');
                this.applyEpisodeLocks();
            }, silent ? 0 : 1500);
        }

        showError(message) {
            this.hideMessages();
            this.errorMessage.textContent = message;
            this.errorMessage.style.display = 'block';
        }

        showSuccess(message) {
            this.hideMessages();
            this.successMessage.textContent = message;
            this.successMessage.style.display = 'block';
        }

        hideMessages() {
            this.errorMessage.style.display = 'none';
            this.successMessage.style.display = 'none';
        }

        // Handle testing password and date
        checkPassword() {
            const enteredPassword = this.passwordInput.value.trim();

            if (!enteredPassword) {
                this.showError('Please enter a password');
                return;
            }

            const normalOk = (enteredPassword === atob(this.password));
            const testPwd = window.ppvsTestPassword ? atob(window.ppvsTestPassword) : '';
            const testOk = testPwd && enteredPassword === testPwd;

            if (normalOk || testOk) {
                let grantedAt = Date.now();
                if (testOk && window.ppvsTestGrantedAt) {
                    const d = new Date(window.ppvsTestGrantedAt + 'T00:00:00');
                    if (!isNaN(d.getTime())) grantedAt = d.getTime();
                }
                this.setCookie({ granted: true, grantedAt, viewed: this.getCookie()?.viewed || {} });
                this.unlockContent();
            } else {
                this.showError('Incorrect password. Please try again.');
                this.passwordInput.value = '';
            }
        }

        // Cookie helpers
        ensureCookie() {
            const data = this.getCookie();
            if (!data) this.setCookie({ granted: false, grantedAt: null, viewed: {} });
        }

        getCookie() {
            const name = 'ppvs_access=';
            const parts = document.cookie.split(';');
            for (let p of parts) {
                p = p.trim();
                if (p.indexOf(name) === 0) {
                    try { return JSON.parse(decodeURIComponent(p.substring(name.length))); } catch (e) { return null; }
                }
            }
            return null;
        }

        setCookie(obj) {
            const expires = new Date(Date.now() + 365*24*60*60*1000).toUTCString();
            document.cookie = `ppvs_access=${encodeURIComponent(JSON.stringify(obj))}; expires=${expires}; path=/; SameSite=Lax`;
        }

        deleteCookie() {
            document.cookie = 'ppvs_access=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax';
        }

        clearCookieIfRequested() {
            try {
                const params = new URLSearchParams(window.location.search);
                const clearAll = params.get('clear-password-cookies') === 'true' || params.get('ppvs-reset') === 'true';
                const clearViewed = params.get('ppvs-clear-viewed') === 'true';
                if (clearAll) {
                    this.deleteCookie();
                    sessionStorage.removeItem('video-series-access');
                } else if (clearViewed) {
                    const data = this.getCookie();
                    if (data) {
                        data.viewed = {};
                        this.setCookie(data);
                    }
                }
            } catch (e) {}
        }

        applyEpisodeLocks() {
            const container = this;
            const isEditor = !!(window.Shopify && Shopify.designMode);
            const data = this.getCookie();
            const grantedAt = data?.grantedAt ? Number(data.grantedAt) : null;
            const granted = Boolean(data?.granted);
            const episodes = this.querySelectorAll('.ai-video-series-episode');

            const bindReview = (ep, id) => {
                const reviewLink = ep.querySelector('.ai-video-series-review-link');
                const reviewDialog = ep.querySelector('.episode-review-dialog[data-episode-id="' + id + '"]');
                if (reviewLink && reviewDialog && !reviewLink._ppvsBound) {
                    reviewLink.addEventListener('click', () => {
                        if (typeof reviewDialog.showDialog === 'function') {
                            reviewDialog.showDialog();
                        } else {
                            // Fallback: dispatch attribute-style handler
                            reviewDialog.querySelector('dialog')?.showModal?.();
                        }
                    });
                    reviewLink._ppvsBound = true;
                }
            };

            episodes.forEach((ep) => {
                const statusEl = ep.querySelector('.ai-video-series-episode-status');
                const delayDays = Number(ep.getAttribute('data-delay-days') || '0');
                const id = ep.getAttribute('data-episode-id');
                const reviewLink = ep.querySelector('.ai-video-series-review-link');
                let available = false;
                let availableDate = null;

                if (isEditor) {
                    // Always show as available in editor
                    ep.classList.remove('locked');
                    if (statusEl) statusEl.textContent = 'Preview';
                    if (reviewLink) reviewLink.removeAttribute('hidden');
                    bindReview(ep, id);
                    // still allow marking viewed during preview for UI, but do not rely on cookie
                    ep.querySelectorAll('.js-episode-trigger').forEach((btn) => {
                        btn.addEventListener('click', () => {
                            if (statusEl) statusEl.textContent = 'Preview';
                            if (reviewLink) reviewLink.removeAttribute('hidden');
                        });
                    });
                    return; // skip normal gating in editor
                }

                if (granted && grantedAt) {
                    const unlockTs = grantedAt + delayDays*24*60*60*1000;
                    available = Date.now() >= unlockTs;
                    availableDate = new Date(unlockTs);
                }

                if (!granted || !available) {
                    ep.classList.add('locked');
                    if (statusEl) {
                        if (!granted && !isEditor) {
                            statusEl.textContent = 'Locked';
                        } else {
                            statusEl.textContent = `Available on ${availableDate.toLocaleDateString()}`;
                        }
                    }
                } else {
                    ep.classList.remove('locked');
                    const viewed = data?.viewed && data.viewed[id];
                    if (statusEl) statusEl.textContent = isEditor ? 'Preview' : (viewed ? 'Watched' : 'New');
                    if (reviewLink) {
                        if (viewed) reviewLink.removeAttribute('hidden'); else reviewLink.setAttribute('hidden', '');
                    }
                    bindReview(ep, id);
                }

                // Mark viewed when dialog opens (click on triggers)
                ep.querySelectorAll('.js-episode-trigger').forEach((btn) => {
                    btn.addEventListener('click', () => {
                        const current = container.getCookie() || { granted: false, grantedAt: null, viewed: {} };
                        current.viewed = current.viewed || {};
                        current.viewed[id] = true;
                        container.setCookie(current);
                        if (statusEl) statusEl.textContent = 'Watched';
                        if (reviewLink) reviewLink.removeAttribute('hidden');
                        bindReview(ep, id);
                    });
                });
            });
        }
    }

    customElements.define('password-protected-video-series', PasswordProtectedVideoSeries);
}